import React, { useState, useEffect } from "react";
import { Table, Input, Button, Spin, Modal, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import LeadLogo from "../../assets/lead_logo.svg";
import { fetchOrders, deleteOrder, cancelShipment } from "../../services/fetchApis";
import UserInfo from "../../components/UserInfo";
import userService from "../../utils/userService";

const PAGE_SIZE = 25;
const TOTAL_RECORDS = 5000; // TODO: Hardcoded total

const OrderListing = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOrderId, setSearchOrderId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [source, setSource] = useState("");
  const [deleteModal, setDeleteModal] = useState({ visible: false, orderId: null });
  const [cancelModal, setCancelModal] = useState({ visible: false, orderId: null, reason: "" });
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    fetchOrderList(pageNumber);

    const user = userService.getUserData();
    if (user && user.email) {
      setUserEmail(user.email);
    }
  }, [pageNumber]);

  const fetchOrderList = async (page, searchOrderId = "", orderSource = "") => {
    setLoading(true);
    try {
      const orderDetails = await fetchOrders(page, searchOrderId, orderSource);
      if (orderDetails?.data) {
        setOrders(orderDetails.data);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmCancelShipment = async () => {
    if (!cancelModal.orderId || !cancelModal.reason.trim()) return;

    try {
      const response = await cancelShipment(cancelModal.orderId, cancelModal.reason);
      if (response.status === "OK") {
        message.success("Shipment cancelled successfully.");
        fetchOrderList(pageNumber);
      } else {
        message.error(response.message || "Failed to cancel shipment.");
      }
    } catch (error) {
      console.error("Error cancelling shipment:", error);
      message.error("Failed to cancel shipment. Please try again.");
    } finally {
      setCancelModal({ visible: false, orderId: null, reason: "" });
    }
  };

  const handleSearch = () => {
    setPageNumber(1); // Reset pagination on search
    fetchOrderList(1, searchOrderId, source);
  };

  const confirmDeleteOrder = async () => {
    if (!deleteModal.orderId) return;

    try {
      await deleteOrder(deleteModal.orderId);
      message.success(`Order ${deleteModal.orderId} deleted successfully.`);
      fetchOrderList(pageNumber); // Refresh list after delete
    } catch (error) {
      console.error("Error deleting order:", error);
      message.error("Failed to delete order. Please try again.");
    } finally {
      setDeleteModal({ visible: false, orderId: null });
    }
  };

  const handleTableChange = (pagination) => {
    setPageNumber(pagination.current);
  };

  const canDeleteOrder = () => {
    const allowedEmails = process.env.REACT_APP_DELETE_ACCESS_EMAILS?.split(",") || [];
    return allowedEmails.includes(userEmail);
  };

  const columns = [
    { title: "Order ID", dataIndex: "orpOrderId", key: "orpOrderId" },
    { title: "Order Source", dataIndex: "source", key: "source" },
    { title: "Date", dataIndex: "createdAt", key: "createdAt", render: (text) => text.split(" ")[0] },
    { title: "Shipment Status", dataIndex: "status", key: "status" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => navigate(`/opp/${record.orpOrderId}`)}>View</Button>
          {canDeleteOrder() && record.status !== "COMPLETED" && (
            <Button type="link" danger onClick={() => setDeleteModal({ visible: true, orderId: record.orpOrderId })}>
              Delete
            </Button>
          )}
          {canDeleteOrder() && record.status === "COMPLETED" && (
            <Button type="link" danger onClick={() => setCancelModal({ visible: true, orderId: record.orpOrderId, reason: "" })}>
              Cancel Shipment
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div className="top-header-inner-container py-3">
        <FontAwesomeIcon icon={faHome} size="2x" style={{ marginRight: "10px", cursor: "pointer" }} onClick={() => navigate(`/`, { replace: true })} />
        <img className="lead-logo" src={LeadLogo} alt="lead-logo" />
        <UserInfo />
      </div>

      {/* SEARCH FILTERS */}
      <div style={{ padding: "20px", marginTop: "80px", display: "flex", gap: "12px", alignItems: "center", justifyContent: "flex-start", flexWrap: "wrap" }}>
        <label htmlFor="source" style={{ fontSize: "14px", fontWeight: "bold" }}>Order Source:</label>
        <select id="source" value={source} onChange={(e) => setSource(e.target.value)}
          style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "14px", width: "150px", minWidth: "100px" }}>
          <option value="">All</option>
          <option value="ORP">ORP</option>
          <option value="OPP">OPP</option>
          <option value="MOCK">MOCK</option>
          <option value="OPP_BULK">OPP_BULK</option>
        </select>

        <Input placeholder="Search by Order ID" value={searchOrderId} onChange={(e) => setSearchOrderId(e.target.value)}
          style={{ width: "200px", minWidth: "150px", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "14px" }} />

        <Button type="primary" onClick={handleSearch} style={{ padding: "8px 16px", backgroundColor: "#007bff", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px" }}>
          Search
        </Button>
      </div>

      {/* ORDER LISTING / LOADER */}
      <div style={{ padding: 20, marginTop: "20px", textAlign: "center" }}>
        {loading ? (
          <Spin size="large" />
        ) : (
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="orpOrderId"
            pagination={{
              current: pageNumber,
              pageSize: PAGE_SIZE,
              total: TOTAL_RECORDS, // Hardcoded total
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal
        title="Confirm Deletion"
        open={deleteModal.visible}
        onOk={confirmDeleteOrder}
        onCancel={() => setDeleteModal({ visible: false, orderId: null })}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete Order ID {deleteModal.orderId}?</p>
      </Modal>
      <Modal
        title="Cancel Shipment"
        open={cancelModal.visible}
        onOk={confirmCancelShipment}
        onCancel={() => setCancelModal({ visible: false, orderId: null, reason: "" })}
        okText="Cancel Shipment"
        okButtonProps={{ danger: true }}
      >
        <p>Enter reason for cancelling shipment:</p>
        <Input.TextArea
          rows={4}
          value={cancelModal.reason}
          onChange={(e) => setCancelModal({ ...cancelModal, reason: e.target.value })}
          placeholder="Reason for cancellation"
        />
      </Modal>
    </div>
  );
};

export default OrderListing;
